/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { MapClusterResponse } from '../models/MapClusterResponse';
import type { MapEventResponse } from '../models/MapEventResponse';
import type { Object } from '../models/Object';
import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';
export class MapService {
    constructor(public readonly httpRequest: BaseHttpRequest) {}
    /**
     * Get individual map events
     * Returns all map-visible events (reports, effraction interventions) within a radius of the viewport center and time window, sorted by distance.
     *
     * **When to use:** Call this endpoint when the map is zoomed in (zoom >= 16) to display individual pins with full details. When zoomed out (zoom < 16), use `GET /map/clusters` instead for aggregated data.
     *
     * **Distance calculation:** By default, `distanceMeters` is measured from the viewport center (`latitude`/`longitude`). Pass `selectedLatitude`/`selectedLongitude` (e.g. the user's home address) to calculate distances from that chosen point instead. The spatial filter (radius) always uses the viewport center.
     *
     * **Event types included:**
     * - `REPORT` — User-generated neighboring reports (with user info, likes, photos)
     * - `INTERVENTION` — Only interventions with confirmed effractions. No private information is exposed — on click the client should display a generic alert (e.g. "Wizz alert — a burglary has been notified in this zone").
     *
     * **Time filter:** Use `hoursAgo` to limit results to events created within the last N hours (default 24). All matching events within the radius and time window are returned — no pagination.
     * @returns MapEventResponse Map events returned successfully.
     * @throws ApiError
     */
    public mapEventsControllerGetMapEvents({
        latitude,
        longitude,
        selectedLatitude,
        selectedLongitude,
        radiusKm = 5,
        hoursAgo = 24,
        types,
    }: {
        /**
         * Latitude of the viewport center point for spatial filtering
         */
        latitude: number,
        /**
         * Longitude of the viewport center point for spatial filtering
         */
        longitude: number,
        /**
         * Latitude of the user-selected location (e.g. home address). distanceMeters is calculated from this point. Falls back to latitude if omitted.
         */
        selectedLatitude?: number,
        /**
         * Longitude of the user-selected location (e.g. home address). distanceMeters is calculated from this point. Falls back to longitude if omitted.
         */
        selectedLongitude?: number,
        /**
         * Search radius in kilometers (default 5)
         */
        radiusKm?: number,
        /**
         * Only return events created within the last N hours (default 24, max 8760)
         */
        hoursAgo?: number,
        /**
         * Filter by event types (comma-separated: REPORT,INTERVENTION)
         */
        types?: Array<'REPORT' | 'INTERVENTION'>,
    }): CancelablePromise<Array<MapEventResponse>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/map/events',
            query: {
                'latitude': latitude,
                'longitude': longitude,
                'selectedLatitude': selectedLatitude,
                'selectedLongitude': selectedLongitude,
                'radiusKm': radiusKm,
                'hoursAgo': hoursAgo,
                'types': types,
            },
            errors: {
                400: `Validation error.`,
                401: `Unauthorized.`,
            },
        });
    }
    /**
     * Get clustered map events
     * Returns spatially clustered map events using square grid aggregation via PostGIS.
     *
     * **When to use:** Call this endpoint when the map is zoomed out (zoom < 16) to reduce payload size. When zoomed in (zoom >= 16), use `GET /map/events` instead to fetch individual event details.
     *
     * **Architecture:**
     * 1. The client detects a Pan/Zoom change and calculates the current Bounding Box (BBOX) and Zoom level.
     * 2. This endpoint receives those parameters and executes a spatial query using PostGIS grid-based clustering.
     * 3. The response contains cluster centroids with pin counts — no heavy individual event data.
     *
     * **Grid cell size:** `gridSize = 500 × 2^(15 − zoom)` meters (continuous, supports fractional zoom).
     *
     * | Zoom  | Grid Size | Scope |
     * |-------|-----------|-------|
     * | 16+   | ≤ 500 m   | Street / Block — use `GET /map/events` instead for individual pins |
     * | 15    | 500 m     | Neighborhood |
     * | 14    | 1 km      | Neighborhood |
     * | 13    | 2 km      | City district |
     * | 12    | 4 km      | City district |
     * | 11    | 8 km      | City |
     * | 10    | 16 km     | City / Region |
     * | 9     | 32 km     | Region |
     * | 8     | 64 km     | Region |
     * | 7     | 128 km    | State / Region |
     * | ≤ 6   | 256 km    | State / Country |
     *
     * **Frontend rendering strategy:**
     * - If `pinCount == 1`: render the standard event icon at the cluster coordinates.
     * - If `pinCount > 1`: render a circular badge component with the count centered. On tap, zoom in to that cluster's coordinates.
     * @returns MapClusterResponse Clustered map events returned successfully.
     * @throws ApiError
     */
    public mapEventsControllerGetMapClusters({
        zoom,
        bbox,
        hoursAgo = 168,
        types,
        subTypes,
    }: {
        /**
         * Current map zoom level (1–20, fractional values supported)
         */
        zoom: number,
        /**
         * Bounding box as "minLng,minLat,maxLng,maxLat" (comma-separated)
         */
        bbox: Object,
        /**
         * Only include events created within the last N hours (default 168 = 7 days, max 8760)
         */
        hoursAgo?: number,
        /**
         * Filter by event types (comma-separated: REPORT,INTERVENTION)
         */
        types?: Array<'REPORT' | 'INTERVENTION'>,
        /**
         * Filter by event sub-types (comma-separated). Report sub-types: SUSPECT_INDIVIDUAL, SUSPICIOUS_VEHICLE, INTRUSION_ATTEMPT, BURGLARY, SPYING_AND_TRACKING, OTHER.
         */
        subTypes?: Array<'SUSPECT_INDIVIDUAL' | 'SUSPICIOUS_VEHICLE' | 'INTRUSION_ATTEMPT' | 'BURGLARY' | 'SPYING_AND_TRACKING' | 'OTHER'>,
    }): CancelablePromise<Array<MapClusterResponse>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/map/clusters',
            query: {
                'zoom': zoom,
                'bbox': bbox,
                'hoursAgo': hoursAgo,
                'types': types,
                'subTypes': subTypes,
            },
            errors: {
                400: `Validation error.`,
                401: `Unauthorized.`,
            },
        });
    }
}
